import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { Keys } from '../../shared/keys';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';
import { DropdownItemSelectionChangeDetail, DropdownSizes, DropdownWbChangeEventDetail, ListBehaviour } from './dropdown.types';
import { DEFAULT_BEHAVIOUR, DEFAULT_SIZE } from './dropdown.constants';

/**
 * @slot - options as wb-dropdown-list
 * @slot label - label slot
 * @slot message - Used to place a wb-control-hint below the input (optional)
 */
@Component({
  tag: 'wb-dropdown',
  styleUrl: 'dropdown.scss',
  shadow: true,
})
export class DropdownComponent implements ComponentInterface {
  private overlayEl: HTMLElement;
  private slotEl: HTMLSlotElement;
  private buttonEl: HTMLButtonElement;

  @Element()
  public el: HTMLElement;

  /** The name of the dropdown component. */
  @Prop({ reflect: true })
  public name?: string;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** The size of the dropdown */
  @Prop({ reflect: true })
  public size?: DropdownSizes = DEFAULT_SIZE;

  /** Disable the component */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Shows the status of the overlay. Do not set manually, use overlay methods instead */
  @Prop({ reflect: true, mutable: true })
  public open = false;

  /** The position of the list */
  @Prop({ reflect: true })
  public listBehaviour?: ListBehaviour = DEFAULT_BEHAVIOUR;

  /** aria-label describing action of icon that user can click */
  @Prop({ reflect: true })
  public buttonActionLabel: string = '';

  @Listen('keydown', { target: 'document' })
  public onKeyDown(event: KeyboardEvent): void {
    if (this.open && event.key === Keys.ESC) {
      this.closeOverlay();
      this.buttonEl.focus();
    }
  }

  @Listen('wbdropdownlistitemselectionchange')
  public onDropdownListItemSelectionChange(event: CustomEvent<DropdownItemSelectionChangeDetail>): void {
    event.stopPropagation();
    this.el.querySelector('[slot="label"]').innerHTML = event.detail.content;
    this.closeOverlay();
    this.wbchange.emit({
      name: this.name,
      ...event.detail,
    });
  }

  /** Emitted when there is a new dropdown selection */
  @Event()
  public wbchange: EventEmitter<DropdownWbChangeEventDetail>;

  /** Toggles the overlay */
  @Method()
  public async toggleOverlay() {
    this.open //
      ? this.closeOverlay()
      : this.openOverlay();
  }

  @Watch('open')
  public onOpenChange(newValue: boolean, oldValue: boolean): void {
    this.setTabindexInOverlay();

    const wasJustOpened = newValue && !oldValue;

    if (wasJustOpened) {
      // needs to run as separate task in event queue
      setTimeout(() => {
        document.addEventListener('click', this.closeOverlayWhenClickedOutside);
      });
    }
  }

  private TagNames = transformTagNames(this, 'wb-dropdown', ['wb-grey-box']);

  private openOverlay = () => {
    this.open = true;
  };

  private closeOverlay = () => {
    this.open = false;

    // remove any no longer needed click-outside-listeners
    document.removeEventListener('click', this.closeOverlayWhenClickedOutside);
  };

  // note: the focusout event behaves quite unexpectedly in combination with Shadow DOM
  private isOverlayStillFocussed = (event: FocusEvent): boolean => {
    const elementReceivingFocus = event.relatedTarget as HTMLElement;

    // case 1: user clicked on a wb-checkbox in the overlay -> keep it open
    if (!elementReceivingFocus) {
      return true;
    }

    // case 2: user clicked on the overlay itself -> keep it open
    if (elementReceivingFocus === this.overlayEl) {
      return true;
    }

    // case 3: user shifts focus using TAB -> only close overlay if elementReceivingFocus is inside the overlay
    return this.el.contains(elementReceivingFocus);
  };

  private onOverlayFocusOut = (event: FocusEvent): void => {
    if (this.open && !this.isOverlayStillFocussed(event)) {
      this.closeOverlay();
    }
  };

  private closeOverlayWhenClickedOutside = (event: MouseEvent) => {
    const isClickInsideOverlay = event.composedPath().includes(this.overlayEl);

    if (!isClickInsideOverlay) {
      this.closeOverlay();
    }
  };

  private setTabindexInOverlay = () => {
    const assignedElements = this.slotEl.assignedElements();

    assignedElements.forEach((element) => {
      if (this.open) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', '-1');
      }
    });
  };

  private bindOverlayRef = (el: HTMLElement): void => {
    this.overlayEl = el;
  };

  private bindButtonRef = (el: HTMLButtonElement): void => {
    this.buttonEl = el;
  };

  private bindSlotRef = (el: HTMLSlotElement): void => {
    this.slotEl = el;
  };

  public render(): JSX.Element {
    const { WbGreyBox } = this.TagNames;

    return (
      <Host class="wb-dropdown" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <WbGreyBox size={this.size} open={this.open} disabled={this.disabled} hasCaretIcon buttonActionLabel={this.buttonActionLabel}>
          <button class="button" type="button" aria-expanded={String(this.open)} tabindex={this.disabled ? '-1' : '0'} aria-label={this.buttonActionLabel} onClick={this.open ? this.closeOverlay : this.openOverlay} ref={this.bindButtonRef} data-testid="wb-dropdown-button">
            <div class="label">
              <slot name="label" />
            </div>
          </button>
        </WbGreyBox>
        <div class="wrapper">
          <slot name="message" />
          <div class="overlay" aria-hidden={String(!this.open)} ref={this.bindOverlayRef} onFocusout={this.onOverlayFocusOut} data-testid="wb-dropdown-overlay">
            <slot onSlotchange={this.setTabindexInOverlay} ref={this.bindSlotRef} />
          </div>
        </div>
      </Host>
    );
  }
}
